// Consultation deep links: reservation id validation and the targets of the links that already
// exist in the wild (legacy emails, new notifications). Pure functions only; the route components
// decide when to navigate.

export const HUB = '/virtual-consultation';
export const RECORD = `${HUB}/reservation-record`;

const HEX32 = /^[0-9a-f]{32}$/i;
const DASHED_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Joinable from 15 minutes before the start until 2 hours after it (same as the reservation list). */
const JOIN_OPENS_MINUTES_BEFORE = 15;
const JOIN_CLOSES_HOURS_AFTER = 2;

/** The id when it is a 32-hex or dashed UUID string, otherwise null. */
export const reservationIdOf = (value) =>
  typeof value === 'string' && (HEX32.test(value) || DASHED_UUID.test(value)) ? value : null;

export const roomPath = (id) => `${HUB}/room/${id}`;

/** The record page with that reservation's detail opened (`id` must already be validated). */
export const recordPath = (id) => `${RECORD}?reservation=${id}`;

/** Legacy email: /virtual-consultation/room?reservation_id=<id> -> the room. */
export const legacyRoomTarget = (search) => {
  const id = reservationIdOf(new URLSearchParams(search).get('reservation_id'));
  return id ? roomPath(id) : HUB;
};

/** Legacy email: /virtual-consultation/summary?reservation_id=<id> -> the record, opened on it. */
export const legacySummaryTarget = (search) => {
  const id = reservationIdOf(new URLSearchParams(search).get('reservation_id'));
  return id ? recordPath(id) : HUB;
};

/** Notification: /virtual-consultation?reservation=<id>. The validated id, or null. */
export const hubReservationId = (search) => reservationIdOf(new URLSearchParams(search).get('reservation'));

/** True inside the join window for a start time (the reservation list's "Join Room" rule). */
export const isWithinJoinWindow = (reservationTime, now = Date.now()) => {
  const start = Date.parse(reservationTime);
  if (Number.isNaN(start)) return false;
  return (
    now > start - JOIN_OPENS_MINUTES_BEFORE * 60_000 && now < start + JOIN_CLOSES_HOURS_AFTER * 3_600_000
  );
};

/** True inside the join window of a confirmed reservation that has a video room. */
export const isJoinableNow = (reservation, now = Date.now()) =>
  Boolean(reservation) &&
  reservation.status === 'confirmed' &&
  Boolean(reservation.has_video_room) &&
  isWithinJoinWindow(reservation.reservation_time, now);

/**
 * Hub rule for ?reservation=<id>: a reservation that can be joined now opens its room; any other
 * reservation opens its detail on the record page.
 */
export const hubTargetFor = (id, reservation, now = Date.now()) =>
  isJoinableNow(reservation, now) ? roomPath(id) : recordPath(id);
